import { useState, useRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { submitReport } from "@api/report.api";
import { extractErrorMessage } from "@api/axios";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  submitStart,
  submitSuccess,
  submitFailure,
  resetSubmission,
} from "@/store/reportsSlice";
import { SeverityLevel } from "@/models/report";
import { MAX_IMAGE_SLOTS, MIN_DESCRIPTION_LENGTH } from "@/constants";

export function useBugReportForm() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const { submitting } = useAppSelector((s) => s.reports);

  const [app, setApp] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<string>(SeverityLevel.High);
  const [images, setImages] = useState<(File | null)[]>(
    Array(MAX_IMAGE_SLOTS).fill(null),
  );
  const [video, setVideo] = useState<File | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const imageInputRefs = Array.from({ length: MAX_IMAGE_SLOTS }, () =>
    useRef<HTMLInputElement>(null),
  );
  const videoInputRef = useRef<HTMLInputElement>(null);

  const usedSlots =
    images.filter((img) => img !== null).length + (video ? 1 : 0);

  const isFormValid = Boolean(
    app &&
    email &&
    description.length >= MIN_DESCRIPTION_LENGTH &&
    severity &&
    recaptchaToken,
  );

  const handleImageSelect = useCallback(
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImages((prev) => {
          const next = [...prev];
          next[index] = file;
          return next;
        });
      }
    },
    [],
  );

  const handleRemoveImage = useCallback((index: number) => {
    setImages((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  }, []);

  const handleVideoSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) setVideo(file);
    },
    [],
  );

  const handleRemoveVideo = useCallback(() => {
    setVideo(null);
  }, []);

  const handleReset = useCallback(() => {
    setApp("");
    setEmail("");
    setDescription("");
    setSeverity(SeverityLevel.High);
    setImages(Array(MAX_IMAGE_SLOTS).fill(null));
    setVideo(null);
    setRecaptchaToken(null);
    recaptchaRef.current?.reset();
    dispatch(resetSubmission());
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!recaptchaToken) {
      notify(t("messages.recaptchaError"), "warning");
      return;
    }

    dispatch(submitStart());

    const formData = new FormData();
    formData.append("app", app);
    formData.append("userEmail", email);
    formData.append("description", description);
    formData.append("level", severity);
    formData.append("recaptchaToken", recaptchaToken);

    images.forEach((img) => {
      if (img) formData.append("images", img);
    });
    if (video) {
      formData.append("video", video);
    }

    try {
      await submitReport(formData);
      dispatch(submitSuccess());
      notify(t("messages.submitSuccess"), "success");
      handleReset();
    } catch (error) {
      const message = extractErrorMessage(error);
      dispatch(submitFailure(message));
      notify(t("messages.submitError"), "error");
    }
  }, [
    app,
    email,
    description,
    severity,
    images,
    video,
    recaptchaToken,
    dispatch,
    notify,
    t,
    handleReset,
  ]);

  return {
    app,
    setApp,
    email,
    setEmail,
    description,
    setDescription,
    severity,
    setSeverity,
    images,
    video,
    recaptchaToken,
    setRecaptchaToken,
    recaptchaRef,
    imageInputRefs,
    videoInputRef,
    usedSlots,
    isFormValid,
    submitting,
    handleImageSelect,
    handleRemoveImage,
    handleVideoSelect,
    handleRemoveVideo,
    handleReset,
    handleSubmit,
  };
}
